import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStoreFullData } from '../services/adminStoreDetailService';
import { updateStore } from '../services/adminStoreService';
import { logActivity } from '../services/adminActivityService';
import { listUsers } from '../services/adminUserService';
import AdminAuthService from '../services/adminAuthService';
import { can } from '../utils/adminPermissions';
import DeliveryTrackingMap from '../components/DeliveryTrackingMap';
import StoreDetailModal from '../components/StoreDetailModal';
import TimeRangeSelector from '../components/TimeRangeSelector';
import { filterByTimeRange, getBucketsForTimeRange, getDateKeyForBucket } from '../utils/timeRange';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { STORE_TABLE_ID, ORDERS_TABLE_ID, REQUESTS_TABLE_ID, PRODUCTS_TABLE_ID } from '../lib/appwrite';
import './StoreDetailPage.css';

const TABS = [
  { id: 'overview', label: 'Overview', icon: '/icons/dashboard-square-01.svg' },
  { id: 'analytics', label: 'Analytics', icon: '/icons/trending-up.svg' },
  { id: 'customers', label: 'Customers', icon: '/icons/2 User.svg' },
  { id: 'products', label: 'Products', icon: '/icons/package.svg' },
  { id: 'orders', label: 'Orders', icon: '/icons/shopping-cart-02.svg' },
  { id: 'payments', label: 'Payment Methods', icon: '/icons/Bag.svg' },
  { id: 'deliveries', label: 'Delivery Requests', icon: '/icons/container-truck.svg' },
  { id: 'activity', label: 'Activity', icon: '/icons/analytics-03.svg' },
];

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  try {
    return dt.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dt.toISOString().replace('T', ' ').slice(0, 16);
  }
};

const formatCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-TZ', { style: 'decimal' });
};

const StoreDetailPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [toggling, setToggling] = useState(false);
  const [trackingRequestId, setTrackingRequestId] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('month');

  const loadData = useCallback(async () => {
    if (!storeId) return;
    try {
      setLoading(true);
      setError(null);
      const [fullData, usersRes, role] = await Promise.all([
        getStoreFullData(storeId),
        listUsers(),
        AdminAuthService.getAdminRole(),
      ]);
      setData(fullData);
      setUsers(usersRes || []);
      setAdminRole(role);
    } catch (err) {
      setError(err.message || 'Failed to load store');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [STORE_TABLE_ID, ORDERS_TABLE_ID, REQUESTS_TABLE_ID, PRODUCTS_TABLE_ID] });

  const owner = data?.store?.ownerId
    ? users.find((u) => (u.uid || u.$id) === data.store.ownerId)
    : null;
  const ownerName = owner
    ? [owner.fname, owner.lname].filter(Boolean).join(' ') || owner.email || '—'
    : '—';
  const ownerContact = owner?.phoneNumber || owner?.email || '—';

  const handleToggleActive = async () => {
    if (!data?.store) return;
    try {
      setToggling(true);
      setError(null);
      await updateStore(data.store.$id, { isActive: !data.store.isActive });
      await logActivity({
        action: 'update',
        resourceType: 'store',
        resourceId: data.store.$id,
        details: `Toggled store active to ${!data.store.isActive}`,
      });
      setData((prev) => ({
        ...prev,
        store: { ...prev.store, isActive: !prev.store.isActive },
      }));
    } catch (err) {
      setError(err.message || 'Failed to update');
    } finally {
      setToggling(false);
    }
  };

  const canUpdate = adminRole && can(adminRole, 'update', 'stores');
  const canApprove = adminRole && (can(adminRole, 'update', 'stores') || can(adminRole, 'approve', 'stores'));

  const totalRevenue = (data?.orders || []).reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);

  if (loading) {
    return (
      <div className="store-detail-loading">
        <div className="dashboard-spinner" />
        <p>Loading store details...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="store-detail-page">
        <button
          type="button"
          className="store-detail-back"
          onClick={() => navigate('/dashboard/stores')}
        >
          <img src="/icons/circle-arrow-left-02.svg" alt="" aria-hidden />
          Back to Stores
        </button>
        <div className="store-detail-error">{error}</div>
      </div>
    );
  }

  const store = data?.store;
  if (!store) return null;

  return (
    <div className="store-detail-page">
      <button
        type="button"
        className="store-detail-back"
        onClick={() => navigate('/dashboard/stores')}
      >
        <img src="/icons/circle-arrow-left-02.svg" alt="" aria-hidden />
        Back to Stores
      </button>

      {error && (
        <div className="store-detail-alert store-detail-alert-error">
          {error}
          <button type="button" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <header className="store-detail-header">
        <div className="store-detail-header-main">
          <div className="store-detail-title-row">
            <h1 className="store-detail-title">{store.storeName || 'Unnamed Store'}</h1>
            <span
              className={`store-detail-badge ${store.isActive ? 'store-detail-badge-active' : 'store-detail-badge-inactive'}`}
            >
              {store.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="store-detail-id">Store ID: {store.$id}</p>
          <div className="store-detail-actions">
            <button
              type="button"
              className="store-detail-btn store-detail-btn-secondary"
              onClick={() => loadData()}
            >
              ⟳ Refresh
            </button>
            {canApprove && (
              <button
                type="button"
                className={`store-detail-btn ${store.isActive ? 'store-detail-btn-warn' : 'store-detail-btn-success'}`}
                onClick={handleToggleActive}
                disabled={toggling}
              >
                {toggling ? '...' : store.isActive ? 'Deactivate' : 'Activate'}
              </button>
            )}
            {canUpdate && (
              <button
                type="button"
                className="store-detail-btn store-detail-btn-primary"
                onClick={() => navigate('/dashboard/stores', { state: { editStoreId: store.$id } })}
              >
                Edit Store
              </button>
            )}
          </div>
        </div>
        <div className="store-detail-stats">
          <div className="store-detail-stat-card">
            <span className="store-detail-stat-value">{data.customers?.length ?? 0}</span>
            <span className="store-detail-stat-label">Customers</span>
          </div>
          <div className="store-detail-stat-card">
            <span className="store-detail-stat-value">{data.products?.length ?? 0}</span>
            <span className="store-detail-stat-label">Products</span>
          </div>
          <div className="store-detail-stat-card">
            <span className="store-detail-stat-value">{data.orders?.length ?? 0}</span>
            <span className="store-detail-stat-label">Orders</span>
          </div>
          <div className="store-detail-stat-card store-detail-stat-revenue">
            <span className="store-detail-stat-value">{formatCurrency(totalRevenue)}</span>
            <span className="store-detail-stat-label">Revenue (TZS)</span>
          </div>
          <div className="store-detail-stat-card">
            <span className="store-detail-stat-value">{data.paymentMethods?.length ?? 0}</span>
            <span className="store-detail-stat-label">Payment Methods</span>
          </div>
        </div>
      </header>

      <nav className="store-detail-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`store-detail-tab ${activeTab === tab.id ? 'store-detail-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="store-detail-tab-icon">
              <img src={tab.icon} alt="" aria-hidden />
            </span>
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="store-detail-content">
        {activeTab === 'overview' && (
          <>
          <div className="store-detail-section">
            <h2>Store Information</h2>
            <div className="store-detail-grid">
              <div className="store-detail-field">
                <label>Store Name</label>
                <span>{store.storeName || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Size</label>
                <span>{store.storeSize || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>TIN</label>
                <span>{store.tin || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Business Number</label>
                <span>{store.businessNumber || '—'}</span>
              </div>
              <div className="store-detail-field store-detail-field-full">
                <label>Owner</label>
                <span>{ownerName}</span>
              </div>
              <div className="store-detail-field store-detail-field-full">
                <label>Owner Contact</label>
                <span>
                  {ownerContact !== '—' ? (
                    <a href={owner?.email ? `mailto:${owner.email}` : `tel:${owner?.phoneNumber}`}>
                      {ownerContact}
                    </a>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div className="store-detail-field">
                <label>Region</label>
                <span>{store.region || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>District</label>
                <span>{store.district || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Ward</label>
                <span>{store.ward || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Street</label>
                <span>{store.street || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Road</label>
                <span>{store.road || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Building Number</label>
                <span>{store.buildingNumber || '—'}</span>
              </div>
              <div className="store-detail-field">
                <label>Surveyed Location</label>
                <span>{store.surveyed || '—'}</span>
              </div>
              <div className="store-detail-field store-detail-field-full">
                <label>Contact</label>
                <span>{store.contact || '—'}</span>
              </div>
              <div className="store-detail-field store-detail-field-full">
                <label>Unsurveyed Address</label>
                <span>{store.unsurveyedAddress || '—'}</span>
              </div>
              {store.surveyed === 'yes' && store.coordinates && Array.isArray(store.coordinates) && store.coordinates.length >= 2 && (
                <div className="store-detail-field store-detail-field-full store-detail-map-wrap">
                  <label>Store Location (Surveyed)</label>
                  <div className="store-detail-map">
                    <iframe
                      title="Store Location"
                      src={`https://www.google.com/maps?q=${store.coordinates[1]},${store.coordinates[0]}&z=17&t=k&output=embed`}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="store-detail-section store-overview-analytics">
            <div className="store-overview-analytics-header">
              <h2>Metrics Preview</h2>
              <button type="button" className="store-overview-analytics-link" onClick={() => setActiveTab('analytics')}>
                View full analytics →
              </button>
            </div>
            <div className="store-overview-metrics-row">
              <div className="store-overview-metric-card">
                <span className="store-overview-metric-value">{data?.storeAnalytics?.totalOrders ?? data?.orders?.length ?? 0}</span>
                <span className="store-overview-metric-label">Orders</span>
              </div>
              <div className="store-overview-metric-card store-overview-metric-revenue">
                <span className="store-overview-metric-value">{formatCurrency(data?.storeAnalytics?.totalRevenue ?? totalRevenue)}</span>
                <span className="store-overview-metric-label">Revenue</span>
              </div>
              <div className="store-overview-metric-card">
                <span className="store-overview-metric-value">{data?.storeAnalytics?.totalCustomers ?? data?.storeAnalytics?.topCustomers?.length ?? data?.customers?.length ?? 0}</span>
                <span className="store-overview-metric-label">Customers</span>
              </div>
              <div className="store-overview-metric-card">
                <span className="store-overview-metric-value">{data?.storeAnalytics?.totalProductTypes ?? data?.storeAnalytics?.topProductsSold?.length ?? data?.products?.length ?? 0}</span>
                <span className="store-overview-metric-label">Products Sold</span>
              </div>
              {data?.storeAnalytics?.deliveryUsage && (() => {
                const top = Object.entries(data.storeAnalytics.deliveryUsage)
                  .filter(([k]) => k !== 'unknown')
                  .sort((a, b) => b[1] - a[1])[0]?.[0];
                if (!top) return null;
                const label = (top.charAt(0) || '').toUpperCase() + top.slice(1);
                return (
                  <div key="top-delivery" className="store-overview-metric-card">
                    <span className="store-overview-metric-value">{label}</span>
                    <span className="store-overview-metric-label">Top Delivery</span>
                  </div>
                );
              })()}
            </div>
            {data?.storeAnalytics?.orderStatusBreakdown && Object.keys(data.storeAnalytics.orderStatusBreakdown).filter((k) => k !== 'unknown' && data.storeAnalytics.orderStatusBreakdown[k] > 0).length > 0 && (
              <div className="store-overview-status-row">
                <span className="store-overview-status-label">Order status:</span>
                <div className="store-overview-status-badges">
                  {Object.entries(data.storeAnalytics.orderStatusBreakdown)
                    .filter(([k, v]) => k !== 'unknown' && v > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([status, count]) => (
                      <span key={status} className="store-overview-status-badge">
                        {(status.charAt(0) || '').toUpperCase() + (status || '').slice(1)}: {count}
                      </span>
                    ))}
                </div>
              </div>
            )}
            <div className="store-overview-preview-grid">
              {data?.storeAnalytics?.topCustomers?.length > 0 && (
                <div className="store-overview-preview-block">
                  <h4>Top Customers</h4>
                  <ul className="store-overview-preview-list">
                    {data.storeAnalytics.topCustomers.slice(0, 5).map((c) => (
                      <li key={c.customerId}>
                        <span>{c.name}</span>
                        <span className="store-overview-preview-meta">{c.orderCount} orders · {formatCurrency(c.totalSpent)}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="store-overview-preview-btn" onClick={() => setActiveTab('analytics')}>
                    See all
                  </button>
                </div>
              )}
              {data?.storeAnalytics?.topProductsSold?.length > 0 && (
                <div className="store-overview-preview-block">
                  <h4>Top Products</h4>
                  <ul className="store-overview-preview-list">
                    {data.storeAnalytics.topProductsSold.slice(0, 5).map((p) => (
                      <li key={p.productName}>
                        <span>{p.productName}</span>
                        <span className="store-overview-preview-meta">{p.quantity} units · {formatCurrency(p.revenue)}</span>
                      </li>
                    ))}
                  </ul>
                  <button type="button" className="store-overview-preview-btn" onClick={() => setActiveTab('analytics')}>
                    See all
                  </button>
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {activeTab === 'analytics' && (() => {
          const allOrders = data?.orders || [];
          const orders = filterByTimeRange(allOrders, analyticsTimeRange);
          const allDeliveries = data?.deliveryRequests || [];
          const deliveries = filterByTimeRange(allDeliveries, analyticsTimeRange, '$createdAt');
          const products = data?.products || [];
          const revenue = orders.reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);
          const avgOrderValue = orders.length > 0 ? revenue / orders.length : 0;
          const completedDeliveries = deliveries.filter((d) =>
            ['completed', 'delivered'].includes((d.status || '').toLowerCase())
          ).length;
          const deliveryRate = deliveries.length > 0
            ? Math.round((completedDeliveries / deliveries.length) * 100)
            : 0;

          const bucketConfig = getBucketsForTimeRange(analyticsTimeRange);
          const byMonth = {};
          bucketConfig.forEach((b) => { byMonth[b.key] = { label: b.label, orders: 0, revenue: 0 }; });
          orders.forEach((o) => {
            const dt = o.orderDate || o.$createdAt || 0;
            const key = getDateKeyForBucket(dt, analyticsTimeRange);
            if (key) {
              if (!byMonth[key]) byMonth[key] = { label: key, orders: 0, revenue: 0 };
              byMonth[key].orders += 1;
              byMonth[key].revenue += parseFloat(o.totalAmount) || 0;
            }
          });

          const productSales = {};
          orders.forEach((o) => {
            const name = o.productName || 'Unknown';
            if (!productSales[name]) productSales[name] = { qty: 0, revenue: 0 };
            productSales[name].qty += parseInt(o.quantity, 10) || 0;
            productSales[name].revenue += parseFloat(o.totalAmount) || 0;
          });
          const topProducts = Object.entries(productSales)
            .map(([name, s]) => ({ name, ...s }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 8);

          const maxOrders = Math.max(1, ...Object.values(byMonth).map((m) => m.orders));
          const maxRev = Math.max(1, ...Object.values(byMonth).map((m) => m.revenue));

          return (
            <div className="store-detail-section">
              <div className="store-detail-section-header">
                <div>
                  <h2>Store Analytics</h2>
                  <p className="store-detail-section-desc">
                    Performance metrics and trends for {store.storeName || 'this store'}
                  </p>
                </div>
                <TimeRangeSelector value={analyticsTimeRange} onChange={setAnalyticsTimeRange} />
              </div>

              <div className="store-analytics-cards">
                <div className="store-analytics-card store-analytics-card-revenue">
                  <span className="store-analytics-card-label">Total Revenue</span>
                  <span className="store-analytics-card-value">{formatCurrency(revenue)}</span>
                  <span className="store-analytics-card-sub">TZS</span>
                </div>
                <div className="store-analytics-card">
                  <span className="store-analytics-card-label">Total Orders</span>
                  <span className="store-analytics-card-value">{orders.length}</span>
                </div>
                <div className="store-analytics-card">
                  <span className="store-analytics-card-label">Avg Order Value</span>
                  <span className="store-analytics-card-value">{formatCurrency(avgOrderValue)}</span>
                </div>
                <div className="store-analytics-card">
                  <span className="store-analytics-card-label">Delivery Completion</span>
                  <span className="store-analytics-card-value">{deliveryRate}%</span>
                  <span className="store-analytics-card-sub">
                    {completedDeliveries} / {deliveries.length}
                  </span>
                </div>
              </div>

              <div className="store-analytics-grid">
                <div className="store-analytics-block">
                  <h3>Revenue & Orders by Month</h3>
                  <div className="store-analytics-bars">
                    {Object.entries(byMonth).map(([key, m]) => (
                      <div key={key} className="store-analytics-bar-row">
                        <span className="store-analytics-bar-label">{m.label}</span>
                        <div className="store-analytics-bar-wrap">
                          <div
                            className="store-analytics-bar store-analytics-bar-orders"
                            style={{ width: `${(m.orders / maxOrders) * 100}%` }}
                            title={`${m.orders} orders`}
                          />
                          <div
                            className="store-analytics-bar store-analytics-bar-revenue"
                            style={{ width: `${(m.revenue / maxRev) * 100}%` }}
                            title={`${formatCurrency(m.revenue)}`}
                          />
                        </div>
                        <span className="store-analytics-bar-legend">
                          {m.orders} orders · {formatCurrency(m.revenue)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="store-analytics-legend">
                    <span><i className="store-analytics-legend-dot orders" /> Orders</span>
                    <span><i className="store-analytics-legend-dot revenue" /> Revenue</span>
                  </div>
                </div>

                <div className="store-analytics-block">
                  <h3>Top Products by Units Sold</h3>
                  {topProducts.length === 0 ? (
                    <p className="store-detail-empty-inline">No sales data yet</p>
                  ) : (
                    <table className="store-detail-table store-analytics-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Units</th>
                          <th>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProducts.map((p) => {
                          const fullProduct = products.find(
                            (pr) => (pr.productName || '').toLowerCase() === (p.name || '').toLowerCase()
                          );
                          return (
                            <tr
                              key={p.name}
                              className={fullProduct ? 'store-detail-row-clickable' : ''}
                              onClick={fullProduct ? () => setDetailModal({ type: 'product', item: fullProduct }) : undefined}
                            >
                              <td>{p.name}</td>
                              <td>{p.qty}</td>
                              <td>{formatCurrency(p.revenue)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="store-analytics-block store-analytics-full">
                <h3>Delivery Status Breakdown</h3>
                <div className="store-analytics-status-grid">
                  {[
                    { status: 'Completed', count: completedDeliveries, key: 'done', s: ['completed', 'delivered'] },
                    { status: 'In Progress', count: deliveries.filter((d) => ['confirm', 'accepted', 'picked', 'delivering'].includes((d.status || '').toLowerCase())).length, key: 'progress', s: [] },
                    { status: 'Pending', count: deliveries.filter((d) => ['pending'].includes((d.status || '').toLowerCase())).length, key: 'pending', s: [] },
                    { status: 'Cancelled', count: deliveries.filter((d) => ['canceled', 'cancelled'].includes((d.status || '').toLowerCase())).length, key: 'cancelled', s: [] },
                  ].map((item) => (
                    <div key={item.key} className="store-analytics-status-item">
                      <span className="store-analytics-status-label">{item.status}</span>
                      <span className="store-analytics-status-value">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Store-level analytics from getStoreAnalyticsDetail */}
              {data?.storeAnalytics && (
                <>
                  <div className="store-analytics-block store-analytics-full">
                    <h3>Top Customers / Buyers</h3>
                    <p className="store-analytics-desc">Customers who bought most from this store</p>
                    {(data.storeAnalytics.topCustomers || []).length === 0 ? (
                      <p className="store-detail-empty-inline">No customer data yet</p>
                    ) : (
                      <table className="store-detail-table store-analytics-table">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Email</th>
                            <th>Orders</th>
                            <th>Total Spent</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.storeAnalytics.topCustomers.slice(0, 15).map((c) => (
                            <tr key={c.customerId} className="store-detail-row-clickable" onClick={() => navigate('/dashboard/orders', { state: { filterCustomer: c.customerId } })}>
                              <td>{c.name}</td>
                              <td>{c.email || '—'}</td>
                              <td>{c.orderCount}</td>
                              <td>{formatCurrency(c.totalSpent)}</td>
                              <td>
                                <button type="button" className="store-analytics-link-btn" onClick={(e) => { e.stopPropagation(); navigate('/dashboard/orders', { state: { filterCustomer: c.customerId } }); }}>
                                  View orders
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="store-analytics-block store-analytics-full">
                    <h3>Products Sold Most</h3>
                    <p className="store-analytics-desc">Best-selling products by quantity and revenue</p>
                    {(data.storeAnalytics.topProductsSold || []).length === 0 ? (
                      <p className="store-detail-empty-inline">No sales data yet</p>
                    ) : (
                      <table className="store-detail-table store-analytics-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Units Sold</th>
                            <th>Revenue</th>
                            <th>Orders</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.storeAnalytics.topProductsSold.map((p) => {
                            const fullProduct = products.find((pr) => (pr.productName || '').toLowerCase() === (p.productName || '').toLowerCase());
                            return (
                              <tr
                                key={p.productName}
                                className={fullProduct ? 'store-detail-row-clickable' : ''}
                                onClick={fullProduct ? () => setDetailModal({ type: 'product', item: fullProduct }) : undefined}
                              >
                                <td>{p.productName}</td>
                                <td>{p.quantity}</td>
                                <td>{formatCurrency(p.revenue)}</td>
                                <td>{p.orderCount}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {data.storeAnalytics.deliveryUsage && Object.keys(data.storeAnalytics.deliveryUsage).filter((k) => k !== 'unknown').length > 0 && (
                    <div className="store-analytics-block store-analytics-full">
                      <h3>Delivery Option Used Most</h3>
                      <div className="store-analytics-badges">
                        {Object.entries(data.storeAnalytics.deliveryUsage)
                          .filter(([k]) => k !== 'unknown')
                          .sort((a, b) => b[1] - a[1])
                          .map(([method, count]) => (
                            <span key={method} className="store-analytics-badge">{method}: {count} orders</span>
                          ))}
                        {(data.storeAnalytics.deliveryUsage.unknown || 0) > 0 && (
                          <span className="store-analytics-badge">Unknown: {data.storeAnalytics.deliveryUsage.unknown} orders</span>
                        )}
                      </div>
                    </div>
                  )}

                  {data.storeAnalytics.orderStatusBreakdown && Object.keys(data.storeAnalytics.orderStatusBreakdown).length > 0 && (
                    <div className="store-analytics-block store-analytics-full">
                      <h3>Order Status Breakdown</h3>
                      <div className="store-analytics-badges">
                        {Object.entries(data.storeAnalytics.orderStatusBreakdown)
                          .sort((a, b) => b[1] - a[1])
                          .map(([status, count]) => (
                            <span key={status} className="store-analytics-badge">{status}: {count}</span>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {activeTab === 'customers' && (
          <div className="store-detail-section">
            <h2>Customers ({data.customers?.length ?? 0})</h2>
            <p className="store-detail-section-desc">
              Buyers who have ordered from this store
            </p>
            <div className="store-detail-table-wrap">
              <table className="store-detail-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Contact</th>
                    <th>Orders</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.customers || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="store-detail-empty">No customers yet</td>
                    </tr>
                  ) : (
                    (data.customers || []).map((c) => (
                      <tr
                        key={c.$id || c.uid}
                        className="store-detail-row-clickable"
                        onClick={() => setDetailModal({ type: 'customer', item: c })}
                      >
                        <td>
                          {[c.fname, c.lname].filter(Boolean).join(' ') || c.email || 'Unknown'}
                        </td>
                        <td>
                          {c.email && (
                            <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}>
                              {c.email}
                            </a>
                          )}
                          {c.email && c.phoneNumber && ' • '}
                          {c.phoneNumber && (
                            <a href={`tel:${c.phoneNumber}`} onClick={(e) => e.stopPropagation()}>
                              {c.phoneNumber}
                            </a>
                          )}
                          {!c.email && !c.phoneNumber && '—'}
                        </td>
                        <td>{c.orderCount ?? 0}</td>
                        <td>
                          <a
                            href={`mailto:${c.email}`}
                            className="store-detail-link-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Email
                          </a>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="store-detail-section">
            <h2>Products ({data.products?.length ?? 0})</h2>
            <div className="store-detail-table-wrap">
              <table className="store-detail-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Stock</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.products || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="store-detail-empty">No products</td>
                    </tr>
                  ) : (
                    (data.products || []).map((p) => (
                      <tr
                        key={p.$id}
                        className="store-detail-row-clickable"
                        onClick={() => setDetailModal({ type: 'product', item: p })}
                      >
                        <td>{p.productName || '—'}</td>
                        <td>{p.productCategory || '—'}</td>
                        <td>{formatCurrency(p.sellPrice)}</td>
                        <td>{p.stockCount ?? '—'}</td>
                        <td>{p.unit || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="store-detail-section">
            <h2>Orders ({data.orders?.length ?? 0})</h2>
            <div className="store-detail-table-wrap">
              <table className="store-detail-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.orders || []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="store-detail-empty">No orders</td>
                    </tr>
                  ) : (
                    (data.orders || []).map((o) => (
                      <tr
                        key={o.$id}
                        className="store-detail-row-clickable"
                        onClick={() => setDetailModal({ type: 'order', item: o })}
                      >
                        <td>{o.orderId || o.$id}</td>
                        <td>{formatDate(o.orderDate)}</td>
                        <td>{o.productName || '—'}</td>
                        <td>{o.quantity ?? '—'}</td>
                        <td>{formatCurrency(o.totalAmount)}</td>
                        <td>
                          <span className={`store-detail-status store-detail-status-${(o.status || '').toLowerCase()}`}>
                            {o.status || '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="store-detail-section">
            <h2>Payment Methods ({data.paymentMethods?.length ?? 0})</h2>
            <div className="store-detail-cards">
              {(data.paymentMethods || []).length === 0 ? (
                <p className="store-detail-empty-inline">No payment methods configured</p>
              ) : (
                (data.paymentMethods || []).map((pm) => (
                  <div key={pm.$id} className="store-detail-card">
                    <div className="store-detail-card-row">
                      <strong>Account Name</strong>
                      <span>{pm.accountName || '—'}</span>
                    </div>
                    <div className="store-detail-card-row">
                      <strong>Method</strong>
                      <span>{pm.paymentMethod || '—'}</span>
                    </div>
                    <div className="store-detail-card-row">
                      <strong>Provider</strong>
                      <span>{pm.provider || '—'}</span>
                    </div>
                    <div className="store-detail-card-row">
                      <strong>Account Number</strong>
                      <span>{pm.accountNumber || '—'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'deliveries' && (
          <div className="store-detail-section">
            <h2>Delivery Requests ({data.deliveryRequests?.length ?? 0})</h2>
            <div className="store-detail-table-wrap">
              <table className="store-detail-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Driver</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.deliveryRequests || []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="store-detail-empty">No delivery requests</td>
                    </tr>
                  ) : (
                    (data.deliveryRequests || []).map((r) => (
                      <tr key={r.$id}>
                        <td>{r.packageName || '—'}</td>
                        <td>{formatDate(r.createdAt || r.$createdAt)}</td>
                        <td>
                          <span className="store-detail-status">
                            {r.status || (r.isComplete ? 'Complete' : r.isTaken ? 'In Progress' : 'Pending')}
                          </span>
                        </td>
                        <td>{r.driverName || r.driverId || '—'}</td>
                        <td>{formatCurrency(r.price)}</td>
                        <td>
                          {(r.dropLatitude && r.dropLongitude) && (r.pickLatitude && r.pickLongitude || r.storeId) ? (
                            <button
                              type="button"
                              className="store-detail-track-btn"
                              onClick={() => setTrackingRequestId(r.$id)}
                            >
                              Track
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="store-detail-section">
            <h2>Recent Activity</h2>
            <div className="store-detail-activity">
              {(data.activity || []).length === 0 ? (
                <p className="store-detail-empty-inline">No recent activity</p>
              ) : (
                (data.activity || []).map((item, i) => (
                  <div key={`${item.type}-${item.id}-${i}`} className="store-detail-activity-item">
                    <span className={`store-detail-activity-icon store-detail-activity-${item.type}`}>
                      {item.type === 'order' && '🛒'}
                      {item.type === 'request' && '🚚'}
                      {item.type === 'conversation' && '💬'}
                    </span>
                    <div className="store-detail-activity-body">
                      <div className="store-detail-activity-title">{item.title}</div>
                      <div className="store-detail-activity-meta">
                        {item.type === 'order' && item.meta?.totalAmount && (
                          <span>{formatCurrency(item.meta.totalAmount)} TZS</span>
                        )}
                        {item.type === 'conversation' && item.meta?.lastMessage && (
                          <span className="store-detail-activity-preview">
                            {String(item.meta.lastMessage).slice(0, 60)}…
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="store-detail-activity-date">{formatDate(item.date)}</div>
                    {item.status && (
                      <span className="store-detail-activity-status">{item.status}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {trackingRequestId && (
        <DeliveryTrackingMap
          requestId={trackingRequestId}
          onClose={() => setTrackingRequestId(null)}
        />
      )}

      {detailModal && (
        <StoreDetailModal
          type={detailModal.type}
          item={detailModal.item}
          data={data}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onClose={() => setDetailModal(null)}
        />
      )}
    </div>
  );
};

export default StoreDetailPage;
